import { useMutation } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo } from "react";

import NotificationBlock from "~/components/notificationBlock/NotificationBlock.tsx";

import type { DomainOrigins } from "../../api/Api";
import { apiClient } from "../../api/ApiClient";
import Button from "../mantine/button/Button";
import Modal from "../ui/modal/Modal";
import Spinner from "../ui/spinner/Spinner";
import styles from "./SiteSpeedTable.module.scss";

type OriginPort = {
  host: string;
  duration: number;
  status: number;
  message?: string;
  error?: string;
};

const getDnsPortTitle = (port: string | number) => {
  switch (String(port)) {
    case "80":
      return "HTTP (80)";
    case "443":
      return "HTTPS (443)";
    default:
      return `Port ${port}`;
  }
};

const responseMessage = (item: OriginPort) => {
  const status = item.status;
  const message = item.message;

  if (status >= 200 && status < 300) {
    return (
      <span className={styles.statusReady}>
        {status} {message || "OK"}
      </span>
    );
  } else if (status >= 300 && status < 400) {
    return (
      <span className={styles.statusWarning}>
        {status} {message || "Redirect"}
      </span>
    );
  } else {
    return (
      <span className={styles.statusError}>
        {status > 0 ? `${status} ` : null}
        {message || "Error"}
      </span>
    );
  }
};

type OriginNested = Record<string, OriginHostPorts>; // {"host.com": {...}}
type OriginHostPorts = Record<string, OriginPort>; // {"80": {...}, "443": {...}}

type StructuredOrigin = OriginPort & { port: number };

export const structureOrigins = (
  data: OriginNested | null | undefined,
): StructuredOrigin[] => {
  const results: StructuredOrigin[] = [];

  if (!data || typeof data !== "object") return results;

  for (const [host, nested] of Object.entries(data)) {
    for (const [port, portData] of Object.entries(nested)) {
      results.push({
        ...portData,
        port: Number(port),
        host: portData.host || host,
      });
    }
  }

  return results;
};

type OriginsStatusModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  domainId: string | number;
  onConfirm?: () => void;
  onError?: (hasError: boolean) => void;
};

const OriginsStatusModal: React.FC<OriginsStatusModalProps> = ({
  isOpen,
  closeModal,
  domainId,
  onConfirm,
  onError,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    mutate: checkOrigins,
    data,
    isPending,
    isSuccess,
    isError,
    reset,
  } = useMutation({
    mutationFn: async () => {
      const response = await apiClient.domains.originsDetail(Number(domainId));

      return structureOrigins(response.data as DomainOrigins);
    },
    onError: () => {
      enqueueSnackbar("Failed to check origins status", {
        variant: "error",
        persist: true,
      });
    },
  });

  // auto start check when modal is opened
  useEffect(() => {
    if (isOpen && !isPending && !isSuccess && !isError) {
      checkOrigins();
    }
  }, [isOpen, isPending, isSuccess, isError, checkOrigins]);

  // reset mutation state when modal is closed
  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  const hasError =
    (Array.isArray(data) &&
      (data.length === 0 ||
        data.some((o) => (o.status ?? 0) >= 400 || o.error))) ||
    (!data && isSuccess);

  // Pass the error state up to the parent
  useEffect(() => {
    if (isSuccess) onError?.(!!hasError);
  }, [isSuccess, hasError, onError]);

  const hasRedirect =
    Array.isArray(data) && data.some((o) => o.status >= 300 && o.status < 400);
  const originsError = !Array.isArray(data) || hasError;

  const groupedByHost = useMemo(() => {
    const map = new Map<string, StructuredOrigin[]>();
    (data ?? []).forEach((o) => {
      const arr = map.get(o.host) ?? [];
      arr.push(o);
      map.set(o.host, arr);
    });

    for (const arr of map.values()) arr.sort((a, b) => a.port - b.port);

    return map;
  }, [data]);

  const handleConfirm = () => {
    onConfirm?.();
    closeModal();
  };

  const handleRetry = () => {
    checkOrigins();
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeModal}
      modalLabel={<h3 className={styles.modalTitle}>Testing Connection</h3>}
    >
      {/* @ts-expect-error TS2339 non-existing className */}
      <div className={styles.modalContent}>
        {isPending && (
          // @ts-expect-error TS2339 non-existing className
          <div className={styles.loading}>
            <Spinner size="2rem" />
            <p>Checking origin connections...</p>
          </div>
        )}

        {!isPending && Array.isArray(data) && data.length > 0 && (
          <>
            <ul className={styles.originsList}>
              {[...groupedByHost.entries()].map(([host, items]) => (
                // @ts-expect-error TS2551 non-existing className
                <li key={host} className={styles.originsItem}>
                  <div>{host}</div>
                  {/* @ts-expect-error TS2339 non-existing className */}
                  <div className={styles.portsData}>
                    {items.map((item) => (
                      // @ts-expect-error TS2339 non-existing className
                      <div key={item.port} className={styles.portRow}>
                        {/* @ts-expect-error TS2339 non-existing className */}
                        <span className={styles.portTitle}>
                          {getDnsPortTitle(item.port)}:{" "}
                        </span>
                        <span>{responseMessage(item)}</span>
                        {item.error && (
                          // @ts-expect-error TS2339 non-existing className
                          <div className={styles.errorText}>{item.error}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </li>
              ))}
            </ul>

            {hasRedirect && (
              // @ts-expect-error TS2339 non-existing className
              <div className={styles.redirectNote}>
                * If you have a redirect from HTTP to HTTPS, the 3<sup>**</sup>{" "}
                response code is most likely fine.
              </div>
            )}
          </>
        )}

        {!isPending &&
          isSuccess &&
          Array.isArray(data) &&
          data.length === 0 && (
            <div>
              <div className={styles.noOrigins}>
                We did not find any sites with AI Visibility Engine enabled for
                this domain.
              </div>

              <div className={styles.noOrigins}>
                <NotificationBlock
                  type="info"
                  message={
                    <>
                      To enable AI Visibility Engine, please go to the{" "}
                      <strong>AI Visibility Engine Status section</strong> above
                      and enable at least one site.
                    </>
                  }
                  withIcon={false}
                />
              </div>
            </div>
          )}
      </div>

      <div className={styles.modalFooter}>
        {!isPending &&
          (!Array.isArray(data) || data.length === 0 || hasError) && (
            <Button
              variant="outlined"
              onClick={handleRetry}
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Spinner size="1rem" /> Loading
                </>
              ) : (
                "Retry"
              )}
            </Button>
          )}
        <Button onClick={handleConfirm} disabled={originsError}>
          Confirm
        </Button>
      </div>
    </Modal>
  );
};

export default OriginsStatusModal;
